import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  CircularProgress,
  Chip,
  Box,
  InputAdornment,
} from "@mui/material";
import { Check, PhotoCamera } from "@mui/icons-material";
import {
  addDoc,
  collection,
  doc,
  updateDoc,
  arrayUnion,
  increment,
  query,
  where,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { format } from "date-fns";
import { db } from "../../firebase";
import { uploadImage } from "../../utils/awsUtils";
import { toast } from "react-toastify";
import { ImageCropModal } from "../Global/ImageCropModal";

const initialForm = {
  firstName: "",
  lastName: "",
  email: "",
  gender: "",
  dob: "",
  phone: "",
  address: "",
  department: [],
  favHymns: "",
  favBibleVerse: "",
};

const DEPARTMENTS = [
  "Choir",
  "Ushering",
  "Prophets",
  "Media",
  "Children's Church",
  "Youth",
  "Welfare",
  "Protocol",
  "Technical",
  "Evangelism",
  "Drama",
  "Member",
];

const abel = { fontFamily: "Abel" };

export const MemberForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [rawImageSrc, setRawImageSrc] = useState("");
  const [familyMatches, setFamilyMatches] = useState([]);
  const [checkingFamily, setCheckingFamily] = useState(false);
  const [selectedMatchIds, setSelectedMatchIds] = useState([]);
  const [familyReviewed, setFamilyReviewed] = useState(false);
  const fileInputRef = useRef(null);

  const toggleMatch = (id) => {
    setSelectedMatchIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setRawImageSrc(URL.createObjectURL(file));
    setCropModalOpen(true);
  };

  const handleCropped = (blob) => {
    setCropModalOpen(false);
    if (blob.size > 2 * 1024 * 1024) {
      toast("Cropped image must be under 2MB", { type: "error" });
      return;
    }
    setImageFile(new File([blob], "profile.jpg", { type: blob.type || "image/jpeg" }));
    setImagePreview(URL.createObjectURL(blob));
  };

  const validate = () => {
    const errs = {};
    if (!form.firstName) errs.firstName = "Required";
    if (!form.lastName) errs.lastName = "Required";
    if (!form.gender) errs.gender = "Required";
    if (!form.phone) errs.phone = "Required";
    if (!form.department.length) errs.department = "Required";
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email))
      errs.email = "Invalid email";
    return errs;
  };

  // Look up existing members that share this last name, so we can ask
  // the person if they belong to that family.
  const findFamilyMatches = async (lastName) => {
    const lastNameLower = lastName.trim().toLowerCase();
    const q = query(
      collection(db, "members"),
      where("lastNameLower", "==", lastNameLower),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  };

  // As soon as a usable last name (surname) is typed, look up existing members who
  // share it so we can surface them as possible family right away.
  useEffect(() => {
    setSelectedMatchIds([]);
    setFamilyReviewed(false);
    const trimmed = form.lastName.trim();
    if (trimmed.length < 2) {
      setFamilyMatches([]);
      setCheckingFamily(false);
      return;
    }
    setCheckingFamily(true);
    const timer = setTimeout(async () => {
      try {
        const matches = await findFamilyMatches(trimmed);
        setFamilyMatches(matches);
      } catch (err) {
        setFamilyMatches([]);
      } finally {
        setCheckingFamily(false);
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.lastName]);

  const finalizeSubmit = async (matches) => {
    setLoading(true);
    try {
      let imgSrc = "";
      let imgId = "";
      if (imageFile) {
        const result = await uploadImage(imageFile);
        imgSrc = result?.imgUrl || "";
        imgId = result?.imgId || "";
      }

      const firstName = form.firstName.trim();
      const lastName = form.lastName.trim();
      const fullName = `${firstName} ${lastName}`.trim();
      const email = form.email.trim();

      const memberRef = await addDoc(collection(db, "members"), {
        firstName,
        lastName,
        lastNameLower: lastName.toLowerCase(),
        name: fullName,
        email,
        gender: form.gender,
        dob: form.dob ? format(new Date(form.dob + "T00:00:00"), "d MMM") : "",
        phone: form.phone,
        address: form.address,
        department: Array.isArray(form.department)
          ? form.department.join(", ")
          : form.department,
        favHymns: form.favHymns ? `CCC Hymn ${form.favHymns.trim()}` : "",
        favBibleVerse: form.favBibleVerse || "",
        imgSrc,
        imgId,
        status: "pending",
        familyId: null,
        createdAt: serverTimestamp(),
      });

      // Family linking is best-effort: the person is already registered at
      // this point (the write above succeeded), so a rules/permission issue
      // on the newer "families" collection shouldn't look like the whole
      // registration failed.
      try {
        const newMemberEntry = { id: memberRef.id, name: fullName, email };
        const selectedMatches = matches || [];
        const asFamilyEntry = (m) => ({
          id: m.id,
          name: m.name || `${m.firstName || ""} ${m.lastName || ""}`.trim(),
          email: m.email || "",
        });
        const existingFamilyId = selectedMatches.find(
          (m) => m.familyId,
        )?.familyId;
        let familyId;

        if (existingFamilyId) {
          // Selected relatives already belong to a family — join it, and
          // backfill anyone else selected who was never actually linked.
          const unlinked = selectedMatches.filter((m) => !m.familyId);
          await updateDoc(doc(db, "families", existingFamilyId), {
            members: arrayUnion(newMemberEntry, ...unlinked.map(asFamilyEntry)),
            familyCount: increment(1 + unlinked.length),
          });
          if (unlinked.length > 0) {
            await Promise.all(
              unlinked.map((m) =>
                updateDoc(doc(db, "members", m.id), {
                  familyId: existingFamilyId,
                }),
              ),
            );
          }
          familyId = existingFamilyId;
        } else {
          // Either the first relative(s) confirmed have no family record yet,
          // or nobody was selected and this is the first of their family to
          // onboard — either way, start a new family record for them.
          const membersForFamily = [
            ...selectedMatches.map(asFamilyEntry),
            newMemberEntry,
          ];
          const familyDocRef = await addDoc(collection(db, "families"), {
            familyName: lastName,
            familyCount: membersForFamily.length,
            members: membersForFamily,
            createdAt: serverTimestamp(),
          });
          familyId = familyDocRef.id;
          if (selectedMatches.length > 0) {
            await Promise.all(
              selectedMatches.map((m) =>
                updateDoc(doc(db, "members", m.id), { familyId }),
              ),
            );
          }
        }

        await updateDoc(memberRef, { familyId });
      } catch (familyErr) {
        toast(
          "You're registered! Family linking couldn't be completed — please let the admin know.",
          { type: "warning" },
        );
      }

      toast("Registration submitted! We'll be in touch.", { type: "success" });
      setSubmitted(true);
      setTimeout(() => navigate("/"), 4000);
    } catch (err) {
      toast(err.message || "Something went wrong. Please try again.", {
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    if (familyMatches.length > 0 && !familyReviewed) {
      toast("Please review the possible family matches shown below.", {
        type: "info",
      });
      return;
    }
    const selected = familyMatches.filter((m) =>
      selectedMatchIds.includes(m.id),
    );
    await finalizeSubmit(selected);
  };

  const isFormComplete =
    form.firstName.trim() &&
    form.lastName.trim() &&
    form.gender &&
    form.phone.trim() &&
    form.address.trim() &&
    form.department.length > 0 &&
    form.favHymns.trim() &&
    form.favBibleVerse.trim() &&
    imageFile !== null;

  const field = (props) => ({
    fullWidth: true,
    color: "secondary",
    margin: "dense",
    inputProps: { style: abel },
    InputLabelProps: { style: abel },
    ...props,
  });

  return (
    <>
      <section style={{ background: "#fff", padding: "56px 16px 80px" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          {submitted ? (
            <div style={{ padding: "48px 0", textAlign: "center" }}>
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  background: "#ffc00022",
                  border: "2px solid #ffc000",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                }}
              >
                <svg viewBox="0 0 20 20" fill="none" width="22" height="22">
                  <path
                    d="M4 10l4 4 8-8"
                    stroke="#000c66"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <Typography
                variant="h5"
                style={{ fontFamily: "Aref Ruqaa, serif", color: "#000c66" }}
              >
                Registration Submitted
              </Typography>
              <Typography
                variant="body2"
                style={{ ...abel, color: "rgba(0,12,102,0.5)" }}
                className="mt-2"
              >
                Welcome to the family! Redirecting home…
              </Typography>
            </div>
          ) : (
            <>
              {/* Photo upload */}
              <div className="flex flex-col items-center mb-4">
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="cursor-pointer relative w-28 h-28 rounded-full overflow-hidden border-2 border-dashed border-secondary flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center text-secondary opacity-60">
                      <PhotoCamera fontSize="medium" />
                      <span className="font-abel text-xs mt-1">Photo</span>
                    </div>
                  )}
                </div>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  style={abel}
                  className="mt-1"
                >
                  Optional · max 2MB
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </div>

              {/* Row 1: First name + Surname */}
              <div className="flex gap-3">
                <TextField
                  {...field({
                    label: "First Name",
                    name: "firstName",
                    value: form.firstName,
                    onChange: handleChange,
                    error: !!errors.firstName,
                    helperText: errors.firstName,
                  })}
                />
                <TextField
                  {...field({
                    label: "Surname",
                    name: "lastName",
                    value: form.lastName,
                    onChange: handleChange,
                    error: !!errors.lastName,
                    helperText: errors.lastName,
                  })}
                />
              </div>

              {checkingFamily && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  style={abel}
                  className="mt-1 block"
                >
                  Checking for family matches…
                </Typography>
              )}

              {!checkingFamily && familyMatches.length > 0 && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "secondary.main",
                    backgroundColor: "rgba(0,12,102,0.03)",
                  }}
                >
                  <Typography variant="body2" style={abel}>
                    We found {familyMatches.length} existing member
                    {familyMatches.length > 1 ? "s" : ""} with the surname "
                    {form.lastName.trim()}". Some surnames belong to more than
                    one unrelated family — select anyone below who is actually
                    part of yours:
                  </Typography>

                  <FormGroup className="mt-1">
                    {familyMatches.map((m) => {
                      const label =
                        m.name ||
                        `${m.firstName || ""} ${m.lastName || ""}`.trim();
                      return (
                        <FormControlLabel
                          key={m.id}
                          control={
                            <Checkbox
                              size="small"
                              color="secondary"
                              checked={selectedMatchIds.includes(m.id)}
                              onChange={() => toggleMatch(m.id)}
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                fontFamily: "Arial, Helvetica, sans-serif",
                              }}
                            >
                              {label}
                            </Typography>
                          }
                        />
                      );
                    })}
                  </FormGroup>

                  {!familyReviewed ? (
                    <div className="flex gap-2 mt-2">
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        style={abel}
                        onClick={() => {
                          setSelectedMatchIds([]);
                          setFamilyReviewed(true);
                        }}
                      >
                        None of These Are My Family
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        style={abel}
                        onClick={() => setFamilyReviewed(true)}
                      >
                        Confirm Selection
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      <Typography
                        variant="caption"
                        style={{
                          ...abel,
                          color: selectedMatchIds.length
                            ? "#1a6b3c"
                            : "inherit",
                        }}
                      >
                        {selectedMatchIds.length > 0
                          ? `✓ Linking with ${selectedMatchIds.length} selected member${selectedMatchIds.length > 1 ? "s" : ""}.`
                          : "Noted — registering as a separate family."}
                      </Typography>
                      <Button
                        size="small"
                        style={abel}
                        onClick={() => setFamilyReviewed(false)}
                      >
                        Change
                      </Button>
                    </div>
                  )}
                </Box>
              )}

              {/* Row 2: Gender + DOB */}
              <div className="flex gap-3 mt-1">
                <FormControl
                  fullWidth
                  margin="dense"
                  color="secondary"
                  error={!!errors.gender}
                >
                  <InputLabel style={abel}>Gender</InputLabel>
                  <Select
                    name="gender"
                    value={form.gender}
                    label="Gender"
                    onChange={handleChange}
                    inputProps={{ style: abel }}
                  >
                    <MenuItem value="Male" style={abel}>
                      Male
                    </MenuItem>
                    <MenuItem value="Female" style={abel}>
                      Female
                    </MenuItem>
                  </Select>
                  {errors.gender && (
                    <Typography variant="caption" color="error" style={abel}>
                      {errors.gender}
                    </Typography>
                  )}
                </FormControl>

                <TextField
                  {...field({
                    label: "Date of Birth",
                    name: "dob",
                    type: "date",
                    value: form.dob,
                    onChange: handleChange,
                    error: !!errors.dob,
                    helperText: errors.dob,
                    InputLabelProps: { shrink: true, style: abel },
                  })}
                />
              </div>

              {/* Row 3: Phone + Email */}
              <div className="flex gap-3 mt-1">
                <TextField
                  {...field({
                    label: "Phone",
                    name: "phone",
                    type: "tel",
                    value: form.phone,
                    onChange: handleChange,
                    error: !!errors.phone,
                    helperText: errors.phone,
                  })}
                />
                <TextField
                  {...field({
                    label: "Email",
                    name: "email",
                    type: "email",
                    value: form.email,
                    onChange: handleChange,
                    error: !!errors.email,
                    helperText: errors.email,
                  })}
                />
              </div>

              {/* Department */}
              <FormControl
                fullWidth
                margin="dense"
                color="secondary"
                error={!!errors.department}
                className="mt-1"
              >
                <InputLabel style={abel}>Department</InputLabel>
                <Select
                  multiple
                  name="department"
                  value={form.department}
                  label="Department"
                  onChange={handleChange}
                  inputProps={{ style: abel }}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((v) => (
                        <Chip key={v} label={v} size="small" style={abel} />
                      ))}
                    </Box>
                  )}
                >
                  {DEPARTMENTS.map((d) => (
                    <MenuItem
                      key={d}
                      value={d}
                      style={abel}
                      sx={{
                        justifyContent: "space-between",
                        "&.Mui-selected": {
                          backgroundColor: "secondary.main",
                          color: "#fff",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "secondary.main",
                        },
                      }}
                    >
                      {d}
                      {form.department.includes(d) && (
                        <Check fontSize="small" />
                      )}
                    </MenuItem>
                  ))}
                </Select>
                {errors.department && (
                  <Typography variant="caption" color="error" style={abel}>
                    {errors.department}
                  </Typography>
                )}
              </FormControl>

              {/* Address */}
              <TextField
                {...field({
                  label: "Home Address",
                  name: "address",
                  value: form.address,
                  onChange: handleChange,
                  error: !!errors.address,
                  helperText: errors.address,
                  placeholder: "12 Church Street, Lagos",
                })}
              />

              {/* Row 4: Fav Hymnal + Fav Bible Verse */}
              <div className="flex gap-3 mt-1">
                <TextField
                  {...field({
                    label: "Favourite Hymn",
                    name: "favHymns",
                    value: form.favHymns,
                    onChange: (e) => {
                      const digits = e.target.value.replace(/\D/g, "");
                      setForm((p) => ({ ...p, favHymns: digits }));
                      setErrors((p) => ({ ...p, favHymns: "" }));
                    },
                    error: !!errors.favHymns,
                    helperText: errors.favHymns,
                    placeholder: "767",
                    inputProps: { inputMode: "numeric" },
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <span
                            style={{
                              ...abel,
                              color: "#888",
                              fontSize: 13,
                              whiteSpace: "nowrap",
                            }}
                          >
                            CCC Hymn
                          </span>
                        </InputAdornment>
                      ),
                    },
                  })}
                />
                <TextField
                  {...field({
                    label: "Favourite Bible Verse",
                    name: "favBibleVerse",
                    value: form.favBibleVerse,
                    onChange: handleChange,
                    error: !!errors.favBibleVerse,
                    helperText: errors.favBibleVerse,
                  })}
                />
              </div>

              <div className="flex justify-center mt-6">
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  color="secondary"
                  size="large"
                  style={abel}
                  disabled={loading || !isFormComplete}
                  startIcon={
                    loading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                  className="!rounded-full !px-10"
                >
                  Submit Registration
                </Button>
              </div>
            </>
          )}
        </div>
      </section>

      <ImageCropModal
        open={cropModalOpen}
        imageSrc={rawImageSrc}
        aspect={1}
        onCancel={() => setCropModalOpen(false)}
        onCropped={handleCropped}
      />
    </>
  );
};
